/**
 * AvatarItem Component
 * Memoized individual avatar item to prevent unnecessary re-renders
 */

import React from 'react';
import { Pressable, Platform } from 'react-native';
import type { AvatarId } from '../../../constants/avatarIds';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { AvatarDisplay } from '../AvatarDisplay';
import { SparkEffect } from './SparkEffect';
import { avatarItemStyles as styles } from './AvatarItem.styles';

export interface AvatarItemProps {
  avatarId: AvatarId;
  isSelected: boolean;
  isPressed: boolean;
  showSpark: boolean;
  onPressIn: (avatarId: AvatarId) => void;
  onPressOut: () => void;
  onPress: (avatarId: AvatarId) => void;
  /** Test ID for testing */
  testID?: string;
}

/**
 * AvatarItem Component
 * Memoized individual avatar item to prevent unnecessary re-renders
 */
export const AvatarItem = React.memo<AvatarItemProps>(({
  avatarId,
  isSelected,
  isPressed,
  showSpark,
  onPressIn,
  onPressOut,
  onPress,
  testID,
}) => {
  const mood = isSelected || isPressed ? 'happy' : 'idle';

  return (
    <Pressable
      style={({ pressed, hovered }) => [
        styles.avatarItem,
        isSelected && styles.avatarItemSelected,
        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
        pressed && styles.avatarItemPressed,
        hovered && Platform.OS === 'web' && styles.avatarItemHover,
      ]}
      onPressIn={() => {
        onPressIn(avatarId);
        // Commit selection on press-in for ALL platforms. Inside the ready-tab
        // ScrollView a tap's onPress can be cancelled by a tiny finger movement,
        // which forced a second tap. Selection is non-destructive (highlight only;
        // Save still applies it), so committing on press-in is safe and snappy.
        onPress(avatarId);
      }}
      onPressOut={onPressOut}
      testID={testID || `avatar-item-${avatarId}`}
    >
      <AvatarDisplay avatarId={avatarId} mood={mood} />
      {showSpark && <SparkEffect isActive={true} />}
    </Pressable>
  );
});

AvatarItem.displayName = 'AvatarItem';

